// deps
import { useContext, useEffect, useMemo, useRef } from "react";

// internal-deps
import { CartContext } from "@cart-client/contexts/CartContext";

// types
import { BOOLEAN_AS_STRING, PresentationInfo } from "@swiggy-private/cart-types";

// utils
import { getCloudinaryRawUrl, getNestedValue } from "@swiggy-private/js-utils";
import { isSwiggyBlack } from "@swiggy-private/super-ui";

// constants
import { LOTTIE_ASSETS, SOUND_ASSETS } from "@swiggy-private/core-ui";

export const usePrefetchBoltLottie = () => {
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;
    const transitionLottieFetchRef = useRef<boolean>(false);

    const swiggyPlanInfo = useMemo(
        () => getNestedValue(cartResponse, "data.swiggyOneInfo.presentationInfo", {} as PresentationInfo),
        [cartResponse],
    );

    const isBlackMembershipPlan = useMemo(() => {
        const { plan_details: planDetails, active_plan_details: activePlanDetails } = swiggyPlanInfo || {};
        return isSwiggyBlack(planDetails) || isSwiggyBlack(activePlanDetails);
    }, [swiggyPlanInfo]);

    const isBlackOTPApplicable = useMemo(() => getNestedValue(cartResponse, "data.swiggyBlackOTPApplicable", false), [
        cartResponse,
    ]);

    const isBoltCartM3 = useMemo(() => cartResponse?.data?.XPData?.bolt_3_0_xp === BOOLEAN_AS_STRING.TRUE, [
        cartResponse,
    ]);

    useEffect(() => {
        if (!isBoltCartM3 || transitionLottieFetchRef.current) return;

        const soundAsset = fetch(getCloudinaryRawUrl(SOUND_ASSETS.SPEEDY_SOUND) || "");
        const lottieAssets: Promise<Response>[] = [
            fetch(getCloudinaryRawUrl(LOTTIE_ASSETS.TRANSITION_LOTTIE_FILE_ID_BOLT) || ""),
        ];
        if (isBlackMembershipPlan && isBlackOTPApplicable) {
            lottieAssets.push(fetch(getCloudinaryRawUrl(LOTTIE_ASSETS.TRANSITION_LOTTIE_FILE_ID_BOLT_OTP) || ""));
        }

        Promise.all([soundAsset, ...lottieAssets])
            .then(() => {
                transitionLottieFetchRef.current = true;
            })
            .catch();
    }, [isBlackMembershipPlan, isBlackOTPApplicable, isBoltCartM3]);
};
